// AdminInventoryReports: Inventory and sales reports with CSV export
import React, { useState } from "react";
import {
	Box,
	Typography,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	CircularProgress,
	Alert,
	Tabs,
	Tab,
	Chip,
	TextField,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import supabase from "../../../supabaseClient";

const REPORT_API = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/inventory-report`;

const REPORTS = [
	{ key: "current_inventory", label: "All Inventory" },
	{ key: "low_stock", label: "Low Stock" },
	{ key: "out_of_stock", label: "Out of Stock" },
	{ key: "sales_by_product", label: "Sales by Product" },
	{ key: "valuation", label: "Inventory Valuation" },
];

const stockChip = (qty, reorderLevel) => {
	if (qty === 0) return <Chip label="Out" color="error" size="small" />;
	if (reorderLevel !== undefined && qty <= reorderLevel) return <Chip label="Low" color="warning" size="small" />;
	return <Chip label="In Stock" color="success" size="small" />;
};

const exportCsv = (data, reportType) => {
	if (!data || data.length === 0) return;
	const headers = Object.keys(data[0]);
	const rows = [
		headers.join(","),
		...data.map((row) =>
			headers.map((h) => JSON.stringify(row[h] ?? "")).join(",")
		),
	];
	const blob = new Blob([rows.join("\n")], { type: "text/csv" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `${reportType}-${new Date().toISOString().slice(0, 10)}.csv`;
	a.click();
	URL.revokeObjectURL(url);
};

export default function AdminInventoryReports() {
	const [tabIndex, setTabIndex] = useState(0);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [since, setSince] = useState("");

	const reportKey = REPORTS[tabIndex].key;

	const fetchReport = async () => {
		setLoading(true);
		setError("");
		setData(null);
		try {
			const { data: { session } } = await supabase.auth.getSession();
			if (!session) throw new Error("Not authenticated");
			const sessionAuthHeader = "Bearer " + session.access_token;

			let url = `${REPORT_API}?type=${reportKey}`;
			if (since && reportKey === "sales_by_product") url += `&since=${since}`;

			const res = await fetch(url, { headers: { Authorization: sessionAuthHeader } });
			const json = await res.json();
			if (!res.ok) throw new Error(json.error || "Failed to load report");
			setData(json.data || []);
		} catch (err) {
			setError(err.message);
		}
		setLoading(false);
	};

	const renderTable = () => {
		if (!data) return null;
		if (data.length === 0) return <Typography variant="body2" color="text.secondary">No data found.</Typography>;

		if (reportKey === "current_inventory") {
			return (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Title</TableCell>
							<TableCell>SKU</TableCell>
							<TableCell>Barcode</TableCell>
							<TableCell align="right">Price</TableCell>
							<TableCell align="center">On Hand</TableCell>
							<TableCell align="center">Reorder At</TableCell>
							<TableCell>Status</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((row) => (
							<TableRow key={row.id}>
								<TableCell>{row.title}</TableCell>
								<TableCell>{row.sku || "—"}</TableCell>
								<TableCell>{row.barcode || "—"}</TableCell>
								<TableCell align="right">${(row.price_cents / 100).toFixed(2)}</TableCell>
								<TableCell align="center">{row.quantity_on_hand}</TableCell>
								<TableCell align="center">{row.reorder_level}</TableCell>
								<TableCell>{stockChip(row.quantity_on_hand, row.reorder_level)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			);
		}

		if (reportKey === "low_stock" || reportKey === "out_of_stock") {
			return (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Title</TableCell>
							<TableCell>SKU</TableCell>
							<TableCell align="right">Price</TableCell>
							<TableCell align="center">On Hand</TableCell>
							<TableCell align="center">Reorder At</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((row) => (
							<TableRow key={row.id}>
								<TableCell>{row.title}</TableCell>
								<TableCell>{row.sku || "—"}</TableCell>
								<TableCell align="right">${(row.price_cents / 100).toFixed(2)}</TableCell>
								<TableCell align="center">{row.quantity_on_hand}</TableCell>
								<TableCell align="center">{row.reorder_level ?? "—"}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			);
		}

		if (reportKey === "sales_by_product") {
			return (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Title</TableCell>
							<TableCell>SKU</TableCell>
							<TableCell align="center">Units Sold</TableCell>
							<TableCell align="right">Revenue</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((row) => (
							<TableRow key={row.store_item_id}>
								<TableCell>{row.title}</TableCell>
								<TableCell>{row.sku || "—"}</TableCell>
								<TableCell align="center">{row.total_qty}</TableCell>
								<TableCell align="right">${(row.total_cents / 100).toFixed(2)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			);
		}

		if (reportKey === "valuation") {
			const totalValuation = data.reduce((sum, r) => sum + r.valuation_cents, 0);
			return (
				<Box>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Title</TableCell>
								<TableCell>SKU</TableCell>
								<TableCell align="center">On Hand</TableCell>
								<TableCell align="right">Unit Price</TableCell>
								<TableCell align="right">Value</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.map((row) => (
								<TableRow key={row.id}>
									<TableCell>{row.title}</TableCell>
									<TableCell>{row.sku || "—"}</TableCell>
									<TableCell align="center">{row.quantity_on_hand}</TableCell>
									<TableCell align="right">${(row.price_cents / 100).toFixed(2)}</TableCell>
									<TableCell align="right">${(row.valuation_cents / 100).toFixed(2)}</TableCell>
								</TableRow>
							))}
							<TableRow>
								<TableCell colSpan={4} align="right">
									<Typography variant="subtitle2">Total Inventory Value</Typography>
								</TableCell>
								<TableCell align="right">
									<Typography variant="subtitle2" fontWeight="bold">
										${(totalValuation / 100).toFixed(2)}
									</Typography>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</Box>
			);
		}

		return null;
	};

	return (
		<Box>
			<Typography variant="h5" gutterBottom>
				Inventory Reports
			</Typography>

			<Tabs
				value={tabIndex}
				onChange={(_, v) => { setTabIndex(v); setData(null); setError(""); }}
				variant="scrollable"
				scrollButtons="auto"
				sx={{ mb: 2 }}
			>
				{REPORTS.map((r) => (
					<Tab key={r.key} label={r.label} />
				))}
			</Tabs>

			{reportKey === "sales_by_product" && (
				<Box mb={2} display="flex" alignItems="center" gap={2}>
					<TextField
						label="Since date (optional)"
						type="date"
						value={since}
						onChange={(e) => setSince(e.target.value)}
						size="small"
						InputLabelProps={{ shrink: true }}
					/>
				</Box>
			)}

			<Box display="flex" gap={2} mb={2}>
				<Button variant="contained" onClick={fetchReport} disabled={loading}>
					{loading ? <CircularProgress size={20} /> : "Run Report"}
				</Button>
				{data && data.length > 0 && (
					<Button
						variant="outlined"
						startIcon={<Download />}
						onClick={() => exportCsv(data, reportKey)}
					>
						Export CSV
					</Button>
				)}
			</Box>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

			{data && (
				<Box sx={{ overflowX: "auto" }}>
					{renderTable()}
				</Box>
			)}
		</Box>
	);
}
